// src/data/events.ts
// Source: "Excel Michele Engagements 2026.xlsx" (Michele, 09.04.26), normalised 09.07.26.
// Dates are ISO calendar days in America/New_York. Bucketing is computed at render time — never edit
// an entry to "move" it between Upcoming and Past.

import { parseISO, startOfDay, isBefore, isAfter, compareAsc, compareDesc, format } from "date-fns";

export type EventType =
  | "talk"
  | "workshop"
  | "book-signing"
  | "author-fair"
  | "podcast"
  | "class"
  | "retreat"
  | "sound-bath"
  | "other";

export interface MicheleEvent {
  id: string;               // kebab-case slug, unique
  title: string;
  topic?: string;           // subtitle line, e.g. the talk's theme
  type: EventType;
  date: string;             // ISO "2026-09-29"
  endDate?: string;         // multi-day retreats and multi-week courses
  time?: string;            // "7:30–9:00 PM"
  startTime?: string;       // "19:30", 24-hour wall clock at the venue; structured data reads this before `time`
  endTime?: string;         // "21:00"; leave out when only a start is published
  timeZone?: string;        // IANA zone of the venue; defaults to "America/New_York"
  venue: string;            // venue, host organisation, or podcast name
  organizer?: string;       // host organisation; structured data falls back to the venue
  organizerUrl?: string;    // organizer site, emitted as organizer.url
  selfHosted?: boolean;     // Michele runs it herself: organizer falls back to Wheat In Chaff Healing Hearts
  address?: string;         // street address when known
  street?: string;          // "340 Veterans Memorial Hwy, Suite 10"
  locality?: string;        // "Commack"
  region?: string;          // "NY"
  postalCode?: string;      // "11725"
  city?: string;            // "Rockville Centre, NY"
  online?: boolean;         // podcasts and virtual events — card shows "Online" instead of city
  price?: string;           // "$25" | "Free"
  description?: string;
  registrationUrl?: string;
  venueUrl?: string;
  image?: string;           // "/events/a-time-for-karma.webp"
  imageAlt?: string;
  flyerPdf?: string;        // "/events/a-time-for-karma-flyer.pdf"
  pageUrl?: string;         // internal page with the full story, e.g. "/new-living-expo"; adds a button on upcoming cards
  recordingUrl?: string;    // podcast episode / video, shown on past cards
  videoUrl?: string;        // public Facebook video/reel URL, embedded via Facebook's video plugin
  videoCredit?: string;     // who posted it, shown under the player
  imageAspect?: string;     // "W / H" — only for flyers that are not the usual portrait, e.g. "980 / 703"
  featured?: boolean;       // styling only (gold border, larger image); never affects order
  detailsTbd?: boolean;     // renders a "Details coming soon" chip
}

export const events: MicheleEvent[] = [
  // ── 2026 ─────────────────────────────────────────────────────────────────────
  {
    id: "2026-02-10-5d-daily",
    title: "5D Daily",
    type: "podcast",
    date: "2026-02-10",
    venue: "5D Daily",
    online: true,
  },
  {
    id: "2026-03-04-ways-to-connect-to-spirit",
    title: "Ways to Connect to Spirit",
    type: "class",
    date: "2026-03-04",
    endDate: "2026-04-22",
    venue: "Nassau Community College",
    city: "Garden City, NY",
  },
  {
    id: "2026-03-20-ascension-retreat-sedona",
    title: "Ascension Retreat",
    topic: "Book pre-launch",
    type: "retreat",
    date: "2026-03-20",
    endDate: "2026-03-22",
    venue: "Sedona Performing Arts Center",
    city: "Sedona, AZ",
  },
  {
    id: "2026-03-26-reiki-certification-1-2",
    title: "Reiki Certification Course, Levels 1 & 2",
    type: "class",
    date: "2026-03-26",
    endDate: "2026-05-14",
    venue: "Great Neck Adult Community Center",
    city: "Great Neck, NY",
  },
  {
    id: "2026-04-18-sound-bath-immersion",
    title: "Sound Bath Immersion",
    type: "sound-bath",
    date: "2026-04-18",
    venue: "Great Neck Library",
    city: "Great Neck, NY",
  },
  {
    id: "2026-06-15-sacred-light-healing-broadcast",
    title: "Sacred Light Healing Broadcast",
    type: "podcast",
    date: "2026-06-15",
    venue: "Sacred Light Healing Broadcast",
    online: true,
  },
  {
    id: "2026-06-26-mystery-of-the-soul",
    title: "Mystery of the Soul Podcast",
    type: "podcast",
    date: "2026-06-26",
    venue: "Mystery of the Soul Podcast",
    online: true,
  },
  {
    id: "2026-08-06-li-healing-arts-author-talk",
    title: "Author Talk & Book Signing",
    topic: "An Evening of Discovery",
    type: "talk",
    date: "2026-08-06",
    venue: "Long Island Healing Arts Center",
    city: "Huntington, NY",
  },
  {
    id: "2026-08-26-brooklyn-womxn-of-color-book-club",
    title: "Book Club Author Talk",
    type: "talk",
    date: "2026-08-26",
    venue: "Brooklyn Womxn of Color Book Club",
    organizer: "Brooklyn Womxn of Color Book Club",
    online: true,
  },
  {
    // Fee, venue and link are from Great Neck Public Schools Community Education's registration listing
    // for this session (checked 09.14.26): $365 resident, $380 non-resident, plus a $25 manual.
    id: "2026-09-10-reiki-certification-1-2-fall",
    title: "Reiki Certification Course, Levels 1 & 2",
    type: "class",
    date: "2026-09-10",
    endDate: "2026-10-22",
    venue: "Cumberland",
    organizer: "Great Neck Public Schools Community Education",
    organizerUrl: "https://www.greatneck.k12.ny.us/programs/community-education",
    address: "30 Cumberland Avenue, Great Neck, NY 11020",
    street: "30 Cumberland Avenue",
    city: "Great Neck, NY",
    locality: "Great Neck",
    region: "NY",
    postalCode: "11020",
    price: "From $365",
    registrationUrl: "https://anc.apm.activecommunities.com/gnpscommunityed/activity/search/detail/6842?onlineSiteId=0&from_original_cui=true",
  },
  {
    id: "2026-09-15-unhindered-soul",
    title: "Unhindered Soul Podcast",
    type: "podcast",
    date: "2026-09-15",
    venue: "Unhindered Soul Podcast",
    online: true,
  },
  {
    id: "2026-09-16-soul-elevation",
    title: "Soul Elevation Podcast",
    type: "podcast",
    date: "2026-09-16",
    venue: "Soul Elevation Podcast",
    online: true,
  },
  {
    id: "2026-09-18-transcendence-retreat-san-diego",
    title: "Transcendence Retreat",
    topic: "Book promotional tour",
    type: "retreat",
    date: "2026-09-18",
    endDate: "2026-09-20",
    // Organizer, passes and venue address are from the San Diego Transcendence Retreat's own site and its
    // Eventbrite listing (checked 09.14.26). Passes run $155 to $733.
    timeZone: "America/Los_Angeles",
    venue: "Catamaran Resort on Mission Bay",
    organizer: "Suzanne Ross Transcendence LLC",
    organizerUrl: "https://sandiegotranscendenceretreat.com/",
    address: "3999 Mission Boulevard, San Diego, CA 92109",
    street: "3999 Mission Boulevard",
    city: "San Diego, CA",
    locality: "San Diego",
    region: "CA",
    postalCode: "92109",
    price: "From $155",
    registrationUrl: "https://sandiegotranscendenceretreat.com/tickets/",
  },
  {
    // Details below are off the "A Gathering" flyer (09.07.26). Zip is not on the flyer; 11725 is
    // corroborated by the venue's own listings, which also match the flyer's phone and suite number.
    id: "2026-09-24-centered-commack-author-talk",
    title: "Author Talk & Book Signing",
    topic: "An Evening of Discovery",
    type: "talk",
    date: "2026-09-24",
    time: "7:00–9:00 PM",
    startTime: "19:00",
    endTime: "21:00",
    venue: "Centered Mind-Body-Spirit",
    organizer: "Centered Mind-Body-Spirit",
    organizerUrl: "https://www.centeredmbs.com",
    address: "340 Veterans Memorial Hwy, Suite 10, Commack, NY 11725",
    street: "340 Veterans Memorial Hwy, Suite 10",
    locality: "Commack",
    region: "NY",
    postalCode: "11725",
    city: "Commack, NY",
    price: "$22",
    description:
      "An evening of discovery for the curious and for anyone looking to uplevel to a higher state of awareness. Expect higher frequencies from integrating your energy with like minded souls, so dress for comfort.",
    venueUrl: "https://www.centeredmbs.com",
    image: "/events/centered-commack-flyer.webp",
    imageAlt: "Flyer for A Gathering, an author talk with Michele London at Centered Mind-Body-Spirit in Commack on September 24",
  },
  {
    id: "2026-09-26-barnes-noble-manhasset",
    title: "Meet Your Next Favorite Local Author",
    topic: "Book fair and signing",
    type: "author-fair",
    date: "2026-09-26",
    time: "1:00 PM",
    startTime: "13:00",       // no published end time
    venue: "Barnes & Noble",
    organizer: "Barnes & Noble Manhasset",
    organizerUrl: "https://stores.barnesandnoble.com/store/1912",
    city: "Manhasset, NY",
    street: "1542 Northern Blvd",
    locality: "Manhasset",
    region: "NY",
    postalCode: "11030",
    price: "Free",
    registrationUrl: "https://stores.barnesandnoble.com/event/9780062218251-0", // B&N lists it as "Local Author Signing"
  },
  {
    id: "2026-09-29-a-time-for-karma",
    title: "Author Talk: An Evening of Discovery",
    topic: "Songs of the Mantis: soul missions, starseeds, cosmic timelines",
    type: "talk",
    date: "2026-09-29",
    time: "7:30–9:00 PM",
    startTime: "19:30",
    endTime: "21:00",
    venue: "A Time For Karma",
    organizer: "A Time For Karma",
    organizerUrl: "https://atimeforkarma.com",
    address: "14 S Village Ave, Rockville Centre, NY 11570",
    street: "14 S Village Ave",
    locality: "Rockville Centre",
    region: "NY",
    postalCode: "11570",
    city: "Rockville Centre, NY",
    price: "$25",
    description:
      "An interactive evening exploring conscious awakening through the lens of a galactic guide, closing with a channeled demonstration. Books available for purchase and signing.",
    registrationUrl: "https://atimeforkarma.com/classes/", // decoded from the flyer QR
    venueUrl: "https://www.atimeforkarma.com",
    image: "/events/a-time-for-karma.webp",
    imageAlt: "Author Talk flyer for Michele London at A Time For Karma, September 29",
    flyerPdf: "/events/a-time-for-karma-flyer.pdf",
    featured: true,
  },
  {
    id: "2026-10-02-soul-in-the-machine",
    // The flyer settles the two names that were previously carried as one combined title: they are
    // a title and a subtitle, so they split back across title/topic. Everything below is off the
    // flyer (09.07.26). No registrationUrl yet — the flyer's QR is decorative and does not decode,
    // so the ticket link is still outstanding with Michele; adding it turns on the Register button.
    title: "The Soul in the Machine",
    topic: "Where Is God in the Age of AI?",
    type: "talk",
    date: "2026-10-02",
    time: "Speaking engagement 7:00–9:00 PM · Psychic readings 9:00–11:00 PM",
    startTime: "19:00",
    endTime: "23:00",
    venue: "Psychic Fest at American Legion Post 1066",
    organizer: "Psychic Fest",
    address: "66 Veterans Blvd, Massapequa, NY 11758",
    street: "66 Veterans Blvd",
    locality: "Massapequa",
    region: "NY",
    postalCode: "11758",
    city: "Massapequa, NY",
    // price unconfirmed
    description:
      "An evening of mediumship, channeled perspectives and conscious conversation on spirituality, consciousness, AI and humanity's future. Michele joins psychic mediums Kristen Tinsley and Joseph D'Airo for the talks; readings follow.",
    image: "/events/psychic-fest-flyer.webp",
    imageAlt: "Psychic Fest flyer: The Soul in the Machine, October 2, American Legion Post 1066, Massapequa",
    videoUrl: "https://www.facebook.com/reel/930898122873410",
    videoCredit: "Reel by co-presenter Kristen Tinsley",
    featured: true,
  },
  {
    id: "2026-10-07-ways-to-connect-to-spirit-fall",
    title: "Ways to Connect to Spirit",
    type: "class",
    date: "2026-10-07",
    endDate: "2026-11-18",
    // Fee, schedule and link are from NCC Continuing Education's catalog (checked 09.14.26).
    time: "Wednesdays, 7:00 to 9:00 PM",
    startTime: "19:00",
    endTime: "21:00",
    venue: "Nassau Community College",
    organizer: "Nassau Community College",
    organizerUrl: "https://www.ncc.edu/continuingeducation/",
    city: "Garden City, NY",
    locality: "Garden City",
    region: "NY",
    price: "$200",
    registrationUrl: "https://continuinged.ncc.edu/nassau/course/course.aspx?C=211&pc=6&mc=27&sc=0",
  },
  {
    // Details below are off the LIHA workshop flyer (09.07.26). registrationUrl is the venue's own
    // page for this workshop, reached from the www.lihealing.com address printed on the flyer.
    id: "2026-10-10-awakening-consciousness-workshop",
    title: "Awakening Consciousness Workshop",
    topic: "Integrating the teachings of Songs of the Mantis",
    type: "workshop",
    date: "2026-10-10",
    time: "10:00 AM–3:00 PM",
    startTime: "10:00",
    endTime: "15:00",
    venue: "Long Island Healing Arts Center",
    organizer: "Long Island Healing Arts Center",
    organizerUrl: "https://www.longislandhealingartslearningcenter.com",
    address: "868 W. Jericho Turnpike, Huntington, NY",
    street: "868 W. Jericho Turnpike",
    locality: "Huntington",
    region: "NY",
    postalCode: "11743",
    city: "Huntington, NY",
    price: "$139",
    description:
      "A full day exploring the deeper teachings behind Songs of the Mantis through discussion, guided exercises, meditation, intuitive practices, channeling and group work. Participants leave with practical ways to embody higher consciousness and a stronger connection with their own soul.",
    registrationUrl: "https://www.longislandhealingartslearningcenter.com/awakeningconsciousness",
    venueUrl: "https://www.longislandhealingartslearningcenter.com",
    image: "/events/awakening-consciousness-flyer.webp",
    imageAlt: "Awakening Consciousness workshop flyer, October 10, Long Island Healing Arts, Huntington",
    flyerPdf: "/events/awakening-consciousness-flyer.pdf",
  },
  {
    // Details below are off the "Introduction to Light Language" flyer (09.07.26), which prints only
    // www.LIHealing.com — venue and address carry over from the same venue's 10.10 flyer.
    // This flyer is landscape, unlike every other one, hence imageAspect.
    id: "2026-11-14-light-language-certification",
    title: "Introduction to Light Language",
    topic: "Certification included: leave able to attune others",
    type: "workshop",
    date: "2026-11-14",
    time: "10:00 AM–2:00 PM",
    startTime: "10:00",
    endTime: "14:00",
    venue: "Long Island Healing Arts Center",
    organizer: "Long Island Healing Arts Center",
    organizerUrl: "https://www.longislandhealingartslearningcenter.com",
    address: "868 W. Jericho Turnpike, Huntington, NY",
    street: "868 W. Jericho Turnpike",
    locality: "Huntington",
    region: "NY",
    postalCode: "11743",
    city: "Huntington, NY",
    price: "$140",
    description:
      "An interactive exchange of energies in a safe environment, where high vibrational energies merge and upgrade your frequency. Everyone practices in class and leaves certified in Light Language, able to attune others. Bring a drum or something to mimic one, plus a notepad for the sigil drawing portion. Pre-registration necessary.",
    registrationUrl: "https://www.longislandhealingartslearningcenter.com/lightlanguageclass",
    venueUrl: "https://www.longislandhealingartslearningcenter.com",
    image: "/events/light-language-flyer.webp",
    imageAlt: "Introduction to Light Language flyer with Michele London, November 14, 10am to 2pm",
    imageAspect: "980 / 703",
  },
  {
    id: "2026-11-16-awakening-consciousness-course",
    title: "Awakening Consciousness Course",
    topic: "Exploring consciousness through the teachings of Songs of the Mantis",
    type: "class",
    date: "2026-11-16",
    endDate: "2026-12-21",
    // Fee, schedule and link are from NCC Continuing Education's catalog (checked 09.14.26).
    time: "Mondays, 7:00 to 9:00 PM",
    startTime: "19:00",
    endTime: "21:00",
    venue: "Nassau Community College",
    organizer: "Nassau Community College",
    organizerUrl: "https://www.ncc.edu/continuingeducation/",
    city: "Garden City, NY",
    locality: "Garden City",
    region: "NY",
    price: "$200",
    registrationUrl: "https://continuinged.ncc.edu/nassau/course/course.aspx?C=237&pc=6&mc=27&sc=0",
  },
  {
    // Booked 09.25.26 through Jannecke Øinæs's Calendly: a recorded video and podcast interview on
    // StreamYard, 18:00 CET on 10.29 (1:00 PM Eastern that week, since Europe leaves DST a week before
    // the US). Recording date, not release date. The StreamYard link is private and stays out of this
    // file; add recordingUrl when the episode publishes, which also turns on its Event markup.
    id: "2026-10-29-wisdom-from-north",
    title: "Wisdom From North",
    topic: "Video and podcast interview with Jannecke\u00a0Øinæs", // no-break space keeps her name on one line
    type: "podcast",
    date: "2026-10-29",
    venue: "Wisdom From North",
    organizer: "Wisdom From North",
    organizerUrl: "https://www.wisdomfromnorth.com",
    online: true,
  },

  // ── 2027 ─────────────────────────────────────────────────────────────────────
  {
    // Booked 09.14.26: a booth plus a 45-minute talk. Dates, hours, venue and counts are from New Living
    // Expo's 2027 welcome email and Exhibitor Kit; pass prices ($33 one day, $55 three days) from
    // newlivingexpo.com (checked 09.29.26). Talk title, room and time are still to come from NLE.
    // The /new-living-expo page carries the full story; the card links to it through pageUrl.
    id: "2027-04-16-new-living-expo",
    title: "New Living Expo 2027",
    topic: "45-minute talk and exhibitor booth",
    type: "talk",
    date: "2027-04-16",
    endDate: "2027-04-18",
    time: "Expo hours: Fri 12 to 9 PM, Sat 9 AM to 9 PM, Sun 9 AM to 7 PM",
    startTime: "12:00",
    endTime: "19:00",
    timeZone: "America/Los_Angeles",
    venue: "Marin Center",
    organizer: "New Living Expo (Portal to Ascension)",
    organizerUrl: "https://newlivingexpo.com",
    address: "10 Avenue of the Flags, San Rafael, CA 94903",
    street: "10 Avenue of the Flags",
    locality: "San Rafael",
    region: "CA",
    postalCode: "94903",
    city: "San Rafael, CA",
    price: "From $33",
    description:
      "Michele London joins the 24th New Living Expo as a speaker and exhibitor: a\u00a045\u2011minute\u00a0talk drawn from Songs\u00a0of\u00a0the\u00a0Mantis, and a booth in the exhibit hall all weekend where you can meet her and take home a signed\u00a0copy. More\u00a0than\u00a03,000\u00a0attendees, 125 exhibitors and 90+ speakers gather for three days of consciousness, wellness and ancient\u00a0wisdom.",
    registrationUrl:
      "https://newlivingexpo.com/?utm_source=michelelondonchanneler.com&utm_medium=referral&utm_campaign=nle2027&utm_content=events-card",
    venueUrl: "https://www.marincenter.org",
    image: "/nle/stage.webp",
    imageAlt: "The New Living Expo main stage, with a speaker in front of the Expo's logo and a seated audience",
    imageAspect: "16 / 9",
    pageUrl: "/new-living-expo",
    featured: true,
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Past once the calendar day after `endDate ?? date` has begun (local time). */
export const isPastEvent = (e: MicheleEvent, today: Date = startOfDay(new Date())) =>
  isBefore(parseISO(e.endDate ?? e.date), today);

/** Multi-day/multi-week entries that have started but not finished. */
export const isInProgress = (e: MicheleEvent, today: Date = startOfDay(new Date())) =>
  !!e.endDate && !isAfter(parseISO(e.date), today) && !isPastEvent(e, today);

export const splitEvents = (list: MicheleEvent[] = events, today: Date = startOfDay(new Date())) => {
  const upcoming = list
    .filter((e) => !isPastEvent(e, today))
    .sort((a, b) => compareAsc(parseISO(a.date), parseISO(b.date)));
  const past = list
    .filter((e) => isPastEvent(e, today))
    .sort((a, b) => compareDesc(parseISO(a.date), parseISO(b.date)));
  return { upcoming, past };
};

/** Keys are "YYYY-MM"; sort() puts them in calendar order. */
export const groupByMonth = (list: MicheleEvent[]) =>
  list.reduce<Record<string, MicheleEvent[]>>((acc, e) => {
    const key = e.date.slice(0, 7);
    (acc[key] ??= []).push(e);
    return acc;
  }, {});

export const monthLabel = (key: string) => format(parseISO(`${key}-01`), "MMMM yyyy");

export const groupByYear = (list: MicheleEvent[]) =>
  list.reduce<Record<string, MicheleEvent[]>>((acc, e) => {
    const y = e.date.slice(0, 4);
    (acc[y] ??= []).push(e);
    return acc;
  }, {});

export const formatEventDate = (e: MicheleEvent) => {
  const start = parseISO(e.date);
  if (!e.endDate) return format(start, "EEEE, MMMM d, yyyy");
  const end = parseISO(e.endDate);
  const sameMonth = format(start, "yyyy-MM") === format(end, "yyyy-MM");
  return sameMonth
    ? `${format(start, "MMMM d")}–${format(end, "d, yyyy")}`
    : `${format(start, "MMMM d")} – ${format(end, "MMMM d, yyyy")}`;
};

export const EVENT_TYPE_LABEL: Record<EventType, string> = {
  talk: "Talk",
  workshop: "Workshop",
  "book-signing": "Book Signing",
  "author-fair": "Author Fair",
  podcast: "Podcast",
  class: "Class",
  retreat: "Retreat",
  "sound-bath": "Sound Bath",
  other: "Appearance",
};
